/**
 * Emails: every email the product sends, as it looks, rendered by the backend
 * with made-up sample data (GET /admin/emails). Read only: nothing is sent and
 * nothing is saved. Emails another service writes (Gleap, Baremetrics) show
 * where they live instead of their content.
 */

import { useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { AlertCircle, Inbox, Loader2 } from 'lucide-react';

import { Badge, type BadgeTone } from '@/components/ui/badge';
import { Card } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { cn } from '@/lib/utils';
import {
  emailsService,
  STATS_PERIODS,
  type EmailPreview,
  type EmailSender,
  type EmailStat,
  type EmailStatus,
  type StatsPeriod,
} from '@/services/emails';

const SENDER_LABELS: Record<EmailSender, string> = {
  roasform: 'ROASForm',
  ghl: 'GHL',
  gleap: 'Gleap',
  baremetrics: 'Baremetrics',
};

/** The status dot in the list and the chip on the email. */
const STATUS: Record<EmailStatus, { label: string; tone: BadgeTone; dot: string }> = {
  live: { label: 'Live', tone: 'success', dot: 'bg-emerald-500' },
  not_switched_on: { label: 'Not switched on', tone: 'warning', dot: 'bg-amber-500' },
  pilot: { label: 'Pilot', tone: 'accent', dot: 'bg-violet-500' },
  once: { label: 'Sent once per version', tone: 'neutral', dot: 'bg-zinc-400' },
};

/** One row of the list: an email, or every version of one (A, B, ...), which the preview switches between. */
export interface EmailListItem {
  key: string;
  name: string;
  versions: EmailPreview[];
}

export interface EmailCategory {
  category: string;
  items: EmailListItem[];
}

/** Groups the emails by category, the categories in the order the API lists them, with an email's versions on one row. */
export function groupByCategory(emails: EmailPreview[]): EmailCategory[] {
  const categories: EmailCategory[] = [];
  for (const email of emails) {
    let category = categories.find((c) => c.category === email.category);
    if (!category) categories.push((category = { category: email.category, items: [] }));
    const key = email.group?.id ?? email.id;
    const item = category.items.find((i) => i.key === key);
    if (item) item.versions.push(email);
    else category.items.push({ key, name: email.group?.name ?? email.name, versions: [email] });
  }
  return categories;
}

/**
 * The email as the iframe shows it: every link opens a new window, which the
 * sandbox blocks, so a click can't load a page into the preview.
 */
export function previewDocument(html: string): string {
  const base = '<base target="_blank">';
  const head = /<head(\s[^>]*)?>/i;
  return head.test(html) ? html.replace(head, (tag) => tag + base) : base + html;
}

/** Who sends it: ROASForm itself, or the service that writes and sends it. */
function SenderBadge({ sender }: { sender: EmailSender }) {
  return (
    <Badge tone={sender === 'roasform' ? 'neutral' : 'accent'} className="shrink-0">
      {SENDER_LABELS[sender]}
    </Badge>
  );
}

export function EmailList({
  categories,
  selectedId,
  onSelect,
}: {
  categories: EmailCategory[];
  selectedId: string | null;
  onSelect: (id: string) => void;
}) {
  return (
    <Card className="gap-0 py-3 px-2 min-w-0">
      <nav aria-label="Emails" className="flex flex-col gap-4 lg:max-h-[calc(100vh-12rem)] lg:overflow-y-auto lg:pr-1">
        {categories.map((category) => (
          <div key={category.category} className="flex flex-col gap-0.5">
            <span className="px-2.5 pb-1 text-xs font-medium uppercase tracking-wide text-muted-foreground">{category.category}</span>
            {category.items.map((item) => {
              const selected = item.versions.some((v) => v.id === selectedId);
              const { sender, status } = item.versions[0];
              return (
                <button
                  key={item.key}
                  type="button"
                  // A row with versions keeps the version on show when it's picked again.
                  onClick={() => onSelect(selected && selectedId ? selectedId : item.versions[0].id)}
                  aria-current={selected ? 'true' : undefined}
                  className={cn('flex items-center gap-2 rounded-md px-2.5 py-1.5 text-left hover:bg-muted', selected && 'bg-muted')}
                >
                  <span aria-hidden="true" className={cn('size-2 shrink-0 rounded-full', STATUS[status].dot)} />
                  <span className={cn('min-w-0 flex-1 text-[13px]', selected && 'font-medium')}>
                    {item.name}
                    <span className="sr-only">, {STATUS[status].label}</span>
                  </span>
                  {sender !== 'roasform' && <SenderBadge sender={sender} />}
                </button>
              );
            })}
          </div>
        ))}
      </nav>
    </Card>
  );
}

/** "34%" of a whole, or a dash when there's nothing to divide by. */
export function percent(part: number, whole: number): string {
  return whole > 0 ? `${Math.round((part / whole) * 100)}%` : '–';
}

/** Both versions need at least this many sends counted for did it before one is called better. */
export const BETTER_MIN_SENDS = 30;

/** The stats are loading (null), failed to load, or loaded: keyed by email id. */
export type StatsState = Record<string, EmailStat> | null | 'failed';

/** The numbers of an email's versions added up; did it only over the versions where it's measured. */
export function totalStats(stats: EmailStat[]) {
  const measured = stats.filter((s) => s.didIt !== null);
  return {
    sent: stats.reduce((n, s) => n + s.sent, 0),
    opened: stats.reduce((n, s) => n + s.opened, 0),
    clicked: stats.reduce((n, s) => n + s.clicked, 0),
    didIt: measured.length ? measured.reduce((n, s) => n + (s.didIt ?? 0), 0) : null,
    didItEligible: measured.reduce((n, s) => n + s.didItEligible, 0),
    /** Sends too young for their did-it window, which don't count yet. */
    tooYoung: stats.reduce((n, s) => n + (s.sent - s.didItEligible), 0),
    labels: [...new Set(stats.map((s) => s.didItLabel))],
    days: Math.max(0, ...stats.map((s) => s.didItDays)),
  };
}

function Tile({ label, value, sub, note, muted }: { label: string; value: string; sub: string; note?: string; muted?: boolean }) {
  return (
    <div className="flex min-w-0 flex-col gap-0.5 rounded-lg border px-3.5 py-3">
      <span className="text-xs text-muted-foreground">{label}</span>
      <span className={cn('text-2xl font-semibold tabular-nums tracking-tight', muted && 'text-muted-foreground')}>{value}</span>
      <span className="text-xs text-muted-foreground">{sub}</span>
      {note && <span className="text-[11px] text-amber-700 dark:text-amber-400">{note}</span>}
    </div>
  );
}

/**
 * How the email did in the period: sent, opened (reads high, Apple Mail opens
 * every email), clicked and did it, then each version against the others.
 * Emails we don't track get one line; Gleap's and Baremetrics' nothing.
 */
export function EmailStats({ versions, stats, days }: { versions: EmailPreview[]; stats: StatsState; days: StatsPeriod }) {
  const sender = versions[0].sender;
  if (stats === null) return <p className="text-[13px] text-muted-foreground">Loading the numbers...</p>;
  if (stats === 'failed') return <p className="text-[13px] text-muted-foreground">Couldn't load the numbers. Reload the page to try again.</p>;
  const rows = versions.flatMap((v) => (stats[v.id] ? [{ email: v, stat: stats[v.id] }] : []));
  if (!rows.length) {
    return sender === 'roasform' ? (
      <p className="text-[13px] text-muted-foreground">Not tracked: this email doesn't record its sends, so it has no numbers.</p>
    ) : null;
  }

  const total = totalStats(rows.map((r) => r.stat));
  const rate = (s: EmailStat) => (s.didIt !== null && s.didItEligible > 0 ? s.didIt / s.didItEligible : null);
  const measured = rows.filter((r) => rate(r.stat) !== null);
  const rates = measured.map((r) => rate(r.stat) as number);
  // Only with enough sends on every measured version, and one clearly ahead.
  const enough = measured.length > 1 && measured.every((r) => r.stat.didItEligible >= BETTER_MIN_SENDS);
  const best = enough && rates.filter((r) => r === Math.max(...rates)).length === 1 ? Math.max(...rates) : null;
  // The period is shorter than the time people get to act: nothing can count yet.
  const periodTooShort = total.didIt !== null && total.didItEligible === 0 && total.days >= days;

  return (
    <div className="flex flex-col gap-3">
      <div className="grid grid-cols-2 gap-2.5 xl:grid-cols-4">
        <Tile label="Sent" value={String(total.sent)} sub={`in the last ${days} days`} />
        <Tile
          label="Opened"
          value={percent(total.opened, total.sent)}
          sub={`${total.opened} of ${total.sent}`}
          note="Apple Mail counts as opened"
          muted
        />
        <Tile label="Clicked" value={percent(total.clicked, total.sent)} sub={`${total.clicked} of ${total.sent}`} />
        <Tile
          label="Did it"
          value={total.didIt === null ? 'Not measured' : periodTooShort ? `Needs ${total.days}+ days` : percent(total.didIt, total.didItEligible)}
          sub={
            total.didIt === null
              ? total.labels.join('; ')
              : periodTooShort
                ? `People get ${total.days} days to act; pick a longer period`
                : `${total.didIt} of ${total.didItEligible}: ${total.labels.length === 1 ? total.labels[0].toLowerCase() : 'each version\'s goal, below'}`
          }
        />
      </div>
      {total.tooYoung > 0 && !periodTooShort && (
        <p className="text-xs text-muted-foreground">
          Sends from the last {total.days} {total.days === 1 ? 'day' : 'days'} aren't counted in Did it yet: their time to act isn't over.
        </p>
      )}
      {rows.length > 1 && (
        <table className="w-full text-[13px] tabular-nums">
          <caption className="caption-bottom pt-2 text-left text-xs text-muted-foreground">
            Each version goes to a different situation, not a random split, so compare them with care.
            {best === null && measured.length > 1 && ` One is marked better once every version has ${BETTER_MIN_SENDS} sends counted.`}
          </caption>
          <thead>
            <tr className="text-xs text-muted-foreground">
              <th scope="col" className="py-1.5 pr-2 text-left font-medium">Version</th>
              <th scope="col" className="py-1.5 px-2 text-right font-medium">Sent</th>
              <th scope="col" className="py-1.5 px-2 text-right font-medium">Clicked</th>
              <th scope="col" className="py-1.5 pl-2 text-right font-medium">Did it</th>
            </tr>
          </thead>
          <tbody>
            {rows.map(({ email, stat }) => {
              const r = rate(stat);
              return (
                <tr key={email.id} className="border-t">
                  <th scope="row" className="py-2 pr-2 text-left font-medium">
                    <span className="inline-flex flex-wrap items-center gap-1.5">
                      {email.version}
                      {best !== null && r === best && (
                        <Badge tone="success" title="Did it most often. The versions go to different situations, not a random split.">
                          Better
                        </Badge>
                      )}
                    </span>
                  </th>
                  <td className="py-2 px-2 text-right">{stat.sent}</td>
                  <td className="py-2 px-2 text-right">{percent(stat.clicked, stat.sent)}</td>
                  <td className="py-2 pl-2">
                    {stat.didIt === null ? (
                      <span className="block text-right text-muted-foreground">Not measured</span>
                    ) : (
                      <span className="flex items-center justify-end gap-2" title={stat.didItLabel}>
                        <span aria-hidden="true" className="h-2 w-24 rounded-full bg-muted">
                          <span className="block h-2 rounded-full bg-foreground" style={{ width: `${Math.round((r ?? 0) * 100)}%` }} />
                        </span>
                        <span className="w-10 text-right">{percent(stat.didIt, stat.didItEligible)}</span>
                      </span>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}
    </div>
  );
}

export function EmailPreviewPanel({
  email,
  versions = [email],
  onVersion,
  stats = {},
  days = 30,
}: {
  email: EmailPreview;
  /** Every version of the email, for the switch; just the email when it has one. */
  versions?: EmailPreview[];
  onVersion?: (id: string) => void;
  stats?: StatsState;
  days?: StatsPeriod;
}) {
  const status = STATUS[email.status];
  return (
    <Card className="gap-0 py-0 overflow-hidden min-w-0">
      <div className="flex flex-col gap-3 px-5 py-4 border-b">
        <div className="flex flex-wrap items-center gap-2">
          <h2 className="text-base font-semibold">{email.group?.name ?? email.name}</h2>
          <Badge tone={status.tone}>{status.label}</Badge>
          <SenderBadge sender={email.sender} />
        </div>
        <dl className="grid grid-cols-[72px_minmax(0,1fr)] gap-x-3 gap-y-1.5 text-[13px]">
          {email.subject !== null && (
            <>
              <dt className="text-muted-foreground">Subject</dt>
              <dd className="font-semibold break-words">{email.subject}</dd>
            </>
          )}
          <dt className="text-muted-foreground">Goes</dt>
          <dd>{email.trigger}</dd>
          <dt className="text-muted-foreground">Who</dt>
          <dd>{email.recipient}</dd>
          {email.group && (
            <>
              <dt className="text-muted-foreground">Versions</dt>
              <dd>{email.group.versions}</dd>
            </>
          )}
        </dl>
        <EmailStats versions={versions} stats={stats} days={days} />
        {versions.length > 1 && (
          <div role="group" aria-label="Version" className="flex flex-wrap gap-1.5">
            {versions.map((v) => {
              const on = v.id === email.id;
              return (
                <button
                  key={v.id}
                  type="button"
                  aria-pressed={on}
                  onClick={() => onVersion?.(v.id)}
                  className={cn(
                    'rounded-md border px-3 py-1 text-[13px] font-medium',
                    on ? 'border-foreground bg-foreground text-background' : 'bg-background hover:bg-muted',
                  )}
                >
                  {v.version}
                </button>
              );
            })}
          </div>
        )}
        {email.note && email.html !== null && (
          <p
            className={cn(
              'rounded-md border px-3 py-2.5 text-[13px]',
              email.status === 'live'
                ? 'bg-muted/50 text-muted-foreground'
                : 'border-amber-200 bg-amber-50 text-amber-900 dark:border-amber-900/50 dark:bg-amber-900/20 dark:text-amber-200',
            )}
          >
            {email.note}
          </p>
        )}
      </div>

      {email.html !== null ? (
        // No scripts, no same origin, no new windows: the email can only be looked at.
        <iframe
          title={`${email.name} email`}
          srcDoc={previewDocument(email.html)}
          sandbox=""
          className="block w-full h-[70vh] min-h-[480px] bg-white"
        />
      ) : (
        <div className="flex flex-col items-center gap-3 px-5 py-12 text-center">
          <Inbox className="size-6 text-muted-foreground" />
          <p className="text-sm text-muted-foreground max-w-md">{email.note}</p>
        </div>
      )}
    </Card>
  );
}

/** Tailwind's lg breakpoint, where the list and the preview sit side by side. */
const SIDE_BY_SIDE = '(min-width: 64rem)';
const REDUCED_MOTION = '(prefers-reduced-motion: reduce)';

export default function EmailsPage() {
  const [params, setParams] = useSearchParams();
  const [emails, setEmails] = useState<EmailPreview[] | null>(null);
  const [failed, setFailed] = useState(false);
  const [days, setDays] = useState<StatsPeriod>(30);
  const [stats, setStats] = useState<StatsState>(null);
  const previewRef = useRef<HTMLElement>(null);

  useEffect(() => {
    let cancelled = false;
    emailsService
      .list()
      .then((data) => !cancelled && setEmails(data))
      .catch(() => !cancelled && setFailed(true));
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    let cancelled = false;
    setStats(null);
    emailsService
      .stats(days)
      .then((data) => !cancelled && setStats(data))
      .catch(() => !cancelled && setStats('failed'));
    return () => {
      cancelled = true;
    };
  }, [days]);

  const categories = useMemo(() => groupByCategory(emails ?? []), [emails]);
  const rows = categories.reduce((n, c) => n + c.items.length, 0);
  const selected = emails?.find((e) => e.id === params.get('email')) ?? emails?.[0] ?? null;
  const versions = selected?.group ? (emails ?? []).filter((e) => e.group?.id === selected.group!.id) : selected ? [selected] : [];

  const select = (id: string) => {
    setParams({ email: id }, { replace: true });
    // Stacked (below lg), the preview sits under the whole list: bring it into view.
    if (!window.matchMedia(SIDE_BY_SIDE).matches) {
      const behavior = window.matchMedia(REDUCED_MOTION).matches ? 'auto' : 'smooth';
      previewRef.current?.scrollIntoView({ behavior, block: 'start' });
    }
  };

  return (
    <div className="space-y-4 sm:space-y-6 w-full max-w-[1310px] mx-auto px-4 sm:px-0">
      <div className="flex flex-col gap-2">
        <div className="flex flex-wrap items-center gap-3">
          <h1 className="text-xl sm:text-2xl font-semibold tracking-tight">
            Emails {emails !== null && <span className="text-muted-foreground font-normal">({rows})</span>}
          </h1>
          <Badge tone="info">Read only</Badge>
          <Select value={String(days)} onValueChange={(v) => setDays(Number(v) as StatsPeriod)}>
            <SelectTrigger aria-label="Period for the numbers" className="ml-auto w-[150px] bg-background">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {STATS_PERIODS.map((d) => (
                <SelectItem key={d} value={String(d)}>
                  Last {d} days
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <p className="text-sm text-muted-foreground max-w-3xl">
          Every email ROASForm sends, as it looks, filled with made-up sample data. Nothing here is sent. Emails Gleap and Baremetrics send are listed
          with where they live.
        </p>
        <p className="text-sm text-muted-foreground max-w-3xl">
          Not shown: Stripe's own emails, such as receipts, and emails from workflows or calendars in our GoHighLevel account.
        </p>
      </div>

      {failed && (
        <Card className="py-4 px-5 flex-row items-center gap-3 text-sm">
          <AlertCircle className="size-4 text-destructive" />
          Couldn't load the emails. Reload the page to try again.
        </Card>
      )}

      {emails === null && !failed && (
        <div role="status" className="flex justify-center py-16">
          <Loader2 className="size-6 animate-spin text-muted-foreground" />
          <span className="sr-only">Loading</span>
        </div>
      )}

      {emails !== null && selected && (
        <div className="grid gap-5 lg:grid-cols-[340px_minmax(0,1fr)] items-start">
          <EmailList categories={categories} selectedId={selected.id} onSelect={select} />
          {/* scroll-mt clears the sticky header when a pick scrolls the preview into view. */}
          <section ref={previewRef} aria-label="Email preview" className="min-w-0 scroll-mt-20">
            <EmailPreviewPanel
              email={selected}
              versions={versions}
              onVersion={(id) => setParams({ email: id }, { replace: true })}
              stats={stats}
              days={days}
            />
          </section>
        </div>
      )}
    </div>
  );
}
